/**
 * Fusion canvas types + visual catalog — implements docs/MULTIMODAL_ENVELOPE_CONTRACT.md
 * and docs/FUSION_CANVAS_ELEMENT_CATALOG.md for the /fusion demo.
 *
 * SELF-CONTAINED on purpose: shares NOTHING with the crypto Workbench (own store,
 * own window.__fusionCy), so it cannot regress the crypto canvas.
 */
export type FusionModule = 'crypto' | 'bank' | 'company' | 'social' | 'network' | 'shared';
export type Determinism = 'deterministic' | 'scored';

export interface Attribution {
  method: string;
  tier: 0 | 1 | 2 | 3;          // 0 cryptographic · 1 legal/notice · 2 deterministic-registry · 3 OSINT/scored
  confidence: number;           // 0..1
  source: string;
  evidence_ref?: string | null;
}

/** A real underlying event under a node — a crypto tx or a bank ledger line. */
export interface TxDetail {
  id: string;
  ref: string;                  // tx_hash (crypto) | UTR/RRN (bank) | post id (social)
  direction: 'in' | 'out';
  counterparty: string;
  value: number;
  unit: string;                 // USDT | INR | …
  ts: string;                   // ISO
  kind?: string;                // 'TRC20' | 'UPI' | 'IMPS' | 'post' …
}

export type FusionSemanticRole =
  | 'suspect_hub'
  | 'p2p_bridge'
  | 'mule_conduit'
  | 'capital_feeder'
  | 'cex_source'
  | 'illicit_sink'
  | 'victim_depositor'
  | 'corporate_anchor';

export interface CryptoDeepDiveMeta {
  chain: string;
  primary_address: string;
  addresses_in_cluster?: string[];
  total_tx_count?: number;
  total_volume_usd?: number;
  token_breakdown?: Record<string, number>;
  freeze_status?: { is_frozen: boolean; issuer: string; checked: boolean };
  feeder_cex?: string;
  workbench_url?: string;
}

export interface FusionNode {
  id: string;                   // "<namespace>:<key>"
  module: FusionModule;
  type: string;
  label: string;
  risk_score?: number;          // 0..1
  /** Evidence attribution. Omitted for presentation-only nodes (group containers). */
  attribution?: Attribution;
  placement_role?: string;
  semantic_role?: FusionSemanticRole;
  cluster_id?: string | null;
  size?: number;
  step?: number;                // ingestion stage it arrives in (1..N)
  txns?: TxDetail[];            // the underlying activity (the "crypto-workbench" detail)
  deep_dive?: CryptoDeepDiveMeta;
  module_extras?: Record<string, unknown>;
}

export interface FusionEdge {
  id: string;
  source: string;
  target: string;
  edge_type: string;            // trace|ownership|affiliation|p2p|proof_anchor|cross_link
  value?: number;
  unit?: string;
  count?: number;
  determinism: Determinism;
  join_key?: string;
  step?: number;
  attribution: Attribution;
  module_extras?: Record<string, unknown>;
}

/** Colour family per module — the "which world" channel. */
export const MODULE_COLOR: Record<FusionModule, string> = {
  crypto:  '#f59e0b',
  bank:    '#2f6fed',
  company: '#7c5cfc',
  social:  '#e0489b',
  network: '#0d9488',
  shared:  '#f59e0b',
};

export const MODULE_LABEL: Record<FusionModule, string> = {
  crypto: 'Crypto', bank: 'Banking', company: 'Company', social: 'Social', network: 'Network', shared: 'Identity',
};

/**
 * Shape per entity type. Three shapes carry the meaning (see ENTITY_CLASS): ellipse = a wallet or
 * person, round-rectangle = an institution or account, diamond = an identifier that bridges worlds.
 */
export const NODE_SHAPE: Record<string, FusionShape> = {
  wallet: 'ellipse', exchange: 'round-rectangle', account: 'round-rectangle', person: 'ellipse',
  company: 'round-rectangle', director: 'ellipse', handle: 'diamond', ip: 'ellipse', device: 'ellipse',
  identity_hub: 'ellipse', cluster: 'round-rectangle',
};

/**
 * Entity classes the canvas distinguishes by border colour and shape (the legend and the
 * stylesheet read this one table). Shapes are limited to three (ellipse, round-rectangle,
 * diamond): ellipse = a wallet or person, round-rectangle = an institution or account,
 * diamond = a bridge between worlds. The class colour is the border; risk is the fill ring.
 */
export type FusionShape = 'ellipse' | 'round-rectangle' | 'diamond';
export interface EntityClassStyle { label: string; color: string; shape: FusionShape }
export const ENTITY_CLASS: Record<string, EntityClassStyle> = {
  identity_hub:     { label: 'Case subject (hub)',  color: '#b45309', shape: 'ellipse' },
  suspect_hub:      { label: 'Suspect hub',         color: '#c2410c', shape: 'ellipse' },
  illicit_sink:     { label: 'Illicit sink',        color: '#dc2626', shape: 'ellipse' },
  mule_conduit:     { label: 'Mule conduit',        color: '#d97706', shape: 'ellipse' },
  victim_depositor: { label: 'Victim / depositor',  color: '#475569', shape: 'ellipse' },
  capital_feeder:   { label: 'Capital feeder',      color: '#2563eb', shape: 'round-rectangle' },
  cex_source:       { label: 'Exchange source',     color: '#2563eb', shape: 'round-rectangle' },
  corporate_anchor: { label: 'Corporate anchor',    color: '#4f46e5', shape: 'round-rectangle' },
  exchange:         { label: 'Exchange',            color: '#64748b', shape: 'round-rectangle' },
  p2p_bridge:       { label: 'P2P bridge',          color: '#9333ea', shape: 'diamond' },
};

/** The class a node is drawn as: its semantic role when known, else its type when that is a class. */
export function entityClassOf(d: Record<string, unknown>): string | null {
  const role = String(d.semantic_role || '');
  if (role in ENTITY_CLASS) return role;
  const type = String(d.type || '');
  if (type === 'sink') return 'illicit_sink';
  if (type in ENTITY_CLASS) return type;
  return null;
}

/** Classes present in a set of element data, in table order, with counts: the Evidence legend rows. */
export function presentEntityClasses(
  elements: { data?: unknown }[],
): { key: string; count: number; style: EntityClassStyle }[] {
  const counts = new Map<string, number>();
  for (const el of elements) {
    const d = (el.data || {}) as Record<string, unknown>;
    if (typeof d.source === 'string' && typeof d.target === 'string') continue;   // edges
    const k = entityClassOf(d);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }
  return Object.keys(ENTITY_CLASS).filter((k) => counts.has(k))
    .map((k) => ({ key: k, count: counts.get(k) as number, style: ENTITY_CLASS[k] }));
}

export const EDGE_COLOR: Record<string, string> = {
  trace: '#64748b', ownership: '#0d9488', affiliation: '#db2777', p2p: '#9333ea',
  proof_anchor: '#2563eb', cross_link: '#d97706', subject_link: '#0891b2',
};

export const EDGE_LABEL: Record<string, string> = {
  trace: 'value flow', ownership: 'owns', affiliation: 'affiliated', p2p: 'UPI ↔ crypto',
  proof_anchor: 'evidence', cross_link: 'same subject', subject_link: 'subject link',
};

export function riskBorder(risk = 0): string {
  if (risk >= 0.66) return '#dc2626';
  if (risk >= 0.33) return '#f59e0b';
  return '#16a34a';
}

/** One arrowhead size for every directed edge. */
export const ARROW_SCALE = 1;
/** Edge label type: horizontal, 9.5px (the report's size). */
export const EDGE_LABEL_FONT = 9.5;
/** Below this rendered size (font px on screen) an edge label is hidden: about zoom 0.74. */
export const EDGE_LABEL_MIN_RENDERED_PX = 7;

/** Polished, data-driven Cytoscape stylesheet. Typed loosely (any[]) — @types/cytoscape's
 *  stylesheet union doesn't cleanly admit function mappers. */
export function fusionStylesheet(): any[] {
  return [
    {
      selector: 'node',
      style: {
        'background-color': (n: any) => MODULE_COLOR[n.data('module') as FusionModule] || '#64748b',
        'background-opacity': 0.96,
        'shape': (n: any) => NODE_SHAPE[n.data('type')] || 'ellipse',
        'label': (n: any) => {
          const base = String(n.data('label') || '');
          const badge = n.data('role_badge');
          return badge ? `${base}\n${badge}` : base;
        },
        'color': '#0f172a',
        'font-size': 9.5,
        'font-weight': 600,
        'font-family': 'Inter, system-ui, sans-serif',
        'text-valign': 'bottom',
        'text-margin-y': 5,
        'text-wrap': 'wrap',
        'text-max-width': '94px',
        'text-outline-color': '#f8fafc',
        'text-outline-width': 2.5,
        'border-width': 2.5,
        'border-color': (n: any) => riskBorder(Number(n.data('risk_score')) || 0),
        'border-opacity': 0.95,
        'width': (n: any) => 26 + Math.min(36, Math.sqrt(Number(n.data('size')) || 1) * 5),
        'height': (n: any) => 26 + Math.min(36, Math.sqrt(Number(n.data('size')) || 1) * 5),
        // Only fade opacity + overlay — NEVER transition width/height (re-animates size on
        // every class churn during replay = the "nodes keep growing" bug). overlay-opacity
        // is safe and gives smooth selection + bloom-on-arrival fades.
        'transition-property': 'opacity, overlay-opacity',
        'transition-duration': '240ms',
      },
    },
    // Ingestion-replay focus. Uses the UNDERLAY channel so it can never repaint a
    // role overlay (illicit_sink red etc.); applied only while a replay/scrub is
    // in progress, never at rest (FusionCanvas.gateStep).
    { selector: 'node.cs-focus-current', style: { 'underlay-color': '#fde047', 'underlay-opacity': 0.45, 'underlay-padding': 7 } },
    { selector: 'node[type="identity_hub"]', style: {
        'width': 62, 'height': 62, 'border-width': 4, 'border-color': ENTITY_CLASS.identity_hub.color,
        'overlay-color': '#fbbf24', 'overlay-opacity': 0.16, 'overlay-padding': 9,
        'font-size': 11.5, 'font-weight': 700, 'z-index': 20 } },
    { selector: 'node[semantic_role="illicit_sink"], node[type="sink"]', style: {
        'shape': ENTITY_CLASS.illicit_sink.shape, 'border-width': 3.5, 'border-color': ENTITY_CLASS.illicit_sink.color,
        'overlay-color': '#ef4444', 'overlay-opacity': 0.24, 'overlay-padding': 8,
        'font-weight': 700, 'z-index': 18 } },
    { selector: 'node[semantic_role="capital_feeder"]', style: {
        'shape': ENTITY_CLASS.capital_feeder.shape, 'border-width': 3, 'border-color': ENTITY_CLASS.capital_feeder.color,
        'overlay-color': '#3b82f6', 'overlay-opacity': 0.16, 'overlay-padding': 7 } },
    { selector: 'node[semantic_role="mule_conduit"]', style: {
        'border-width': 3.5, 'border-color': ENTITY_CLASS.mule_conduit.color, 'font-weight': 700 } },
    { selector: 'node[semantic_role="p2p_bridge"]', style: {
        'shape': ENTITY_CLASS.p2p_bridge.shape, 'border-width': 3, 'border-color': ENTITY_CLASS.p2p_bridge.color,
        'overlay-color': '#a855f7', 'overlay-opacity': 0.16, 'overlay-padding': 7 } },
    { selector: 'node[semantic_role="suspect_hub"]', style: {
        'border-width': 4, 'border-color': ENTITY_CLASS.suspect_hub.color, 'font-weight': 700, 'z-index': 19 } },
    { selector: 'node[semantic_role="corporate_anchor"]', style: {
        'shape': ENTITY_CLASS.corporate_anchor.shape, 'border-width': 3, 'border-color': ENTITY_CLASS.corporate_anchor.color } },
    { selector: 'node[semantic_role="victim_depositor"]', style: {
        'border-width': 3, 'border-style': 'double', 'border-color': ENTITY_CLASS.victim_depositor.color } },
    { selector: 'node[semantic_role="cex_source"]', style: {
        'shape': ENTITY_CLASS.cex_source.shape, 'border-width': 3, 'border-color': ENTITY_CLASS.cex_source.color } },
    { selector: 'node[type="exchange"]', style: { 'border-width': 3 } },
    { selector: 'node[type="cluster"]', style: {
        'background-opacity': 0.14, 'border-style': 'dashed', 'border-color': '#64748b',
        'border-width': 2, 'width': 88, 'height': 54, 'font-size': 9, 'shape': 'round-rectangle' } },
    // level=high relay clusters (lossless; expand from the Inspector or by double-click).
    { selector: 'node[member_count]', style: { 'text-valign': 'center', 'text-margin-y': 0, 'font-weight': 700 } },
    // Compound group containers (any node that has children) — structure, not a class.
    { selector: 'node:parent', style: {
        'background-opacity': 0, 'border-width': 2, 'border-color': '#94a3b8', 'border-style': 'dashed',
        'shape': 'round-rectangle', 'text-valign': 'top', 'text-margin-y': -4, 'padding': '14px' } },
    { selector: 'node[placement_role="shared_relay"]', style: { 'overlay-color': '#fbbf24', 'overlay-opacity': 0.14, 'overlay-padding': 8 } },
    // Macro view structure (components/fusion/fusionMacro.ts). Lanes and group
    // boxes are compound nodes recognised by `type` — structure, not classes.
    // A lane does not take events, so a tap on its background reaches the canvas.
    { selector: 'node[type="phase_lane"]', style: {
        'shape': 'round-rectangle', 'background-color': '#e2e8f0', 'background-opacity': 0.3,
        'border-width': 1, 'border-style': 'solid', 'border-color': '#cbd5e1',
        'label': 'data(label)', 'text-valign': 'top', 'text-halign': 'center', 'text-margin-y': -6,
        'font-size': 12, 'font-weight': 700, 'color': '#334155', 'text-transform': 'uppercase',
        'padding': '18px', 'events': 'no' } },
    { selector: 'node[type="group_box"]', style: {
        'shape': 'round-rectangle', 'background-color': '#f59e0b', 'background-opacity': 0.06,
        'border-width': 1.5, 'border-style': 'dashed', 'border-color': '#64748b',
        'text-valign': 'top', 'text-halign': 'center', 'text-margin-y': -4,
        'text-wrap': 'wrap', 'text-max-width': '240px',
        'font-size': 9.5, 'font-weight': 700, 'color': '#1e293b', 'padding': '14px' } },
    // A folded group box: one card with the name, member count and per-token total inside it.
    { selector: 'node[type="group_box"][?collapsed]', style: {
        'width': 190, 'height': 70, 'background-color': '#fffbeb', 'background-opacity': 1,
        'border-width': 2, 'border-style': 'solid', 'border-color': '#94a3b8',
        'text-valign': 'center', 'text-halign': 'center', 'text-margin-y': 0,
        'text-wrap': 'wrap', 'text-max-width': '176px', 'font-size': 9.5, 'font-weight': 700, 'color': '#1e293b' } },
    { selector: 'node[type="group_box"][?big_group]', style: { 'width': 250, 'height': 96, 'text-max-width': '236px' } },
    // Actions layer: notice nodes per seizure target; a target without a deposit
    // tx hash is marked, never hidden.
    { selector: 'node[type="notice"]', style: {
        'shape': 'round-rectangle', 'background-color': '#dbeafe', 'background-opacity': 1,
        'border-width': 2, 'border-style': 'solid', 'border-color': '#2563eb',
        'width': 136, 'height': 56, 'text-valign': 'center', 'text-halign': 'center', 'text-margin-y': 0,
        'text-wrap': 'wrap', 'text-max-width': '128px', 'font-size': 8.5, 'color': '#1e3a8a' } },
    { selector: 'node[?evidence_missing]', style: {
        'border-color': '#dc2626', 'border-style': 'dashed', 'border-width': 3, 'color': '#991b1b' } },
    { selector: 'node[?action_target]', style: {
        'underlay-color': '#2563eb', 'underlay-opacity': 0.28, 'underlay-padding': 9 } },
    // Related case (NS-58): the node also appears in a case the officer linked to this one. Decorative ring
    // only (never opacity, R11); the data flag is declared by case-canvas/relatedCases.ts.
    { selector: 'node[?related_case]', style: {
        'outline-width': 3, 'outline-color': '#7c3aed', 'outline-style': 'double', 'outline-offset': 3 } },
    // CANVAS03: node with officer notes gets a visible double border marker (no opacity per R11/R23).
    { selector: 'node[?has_notes]', style: {
        'border-style': 'double', 'border-width': 3.5, 'border-color': '#0284c7' } },
    {
      selector: 'edge',
      style: {
        'curve-style': 'bezier',
        'width': (e: any) => 1.4 + Math.min(5, Math.log10((Number(e.data('value')) || 1) + 1)),
        'line-color': (e: any) => EDGE_COLOR[e.data('edge_type')] || '#94a3b8',
        'target-arrow-color': (e: any) => EDGE_COLOR[e.data('edge_type')] || '#94a3b8',
        'target-arrow-shape': 'triangle',
        'arrow-scale': ARROW_SCALE,
        'line-style': (e: any) => (e.data('determinism') === 'scored' ? 'dashed' : 'solid'),
        'opacity': (e: any) => (e.data('determinism') === 'scored' ? 0.5 : 0.92),
        'label': 'data(_edgeLabel)',
        'font-size': EDGE_LABEL_FONT,
        'font-family': 'Inter, system-ui, sans-serif',
        'color': '#475569',
        // Horizontal, like the report: rotated text along a fan of edges is unreadable. Hidden
        // while the rendered size is below the threshold (zoomed out); shown on hover and select.
        'text-rotation': 'none',
        'min-zoomed-font-size': EDGE_LABEL_MIN_RENDERED_PX,
        'text-background-color': '#ffffff',
        'text-background-opacity': 0.88,
        'text-background-shape': 'roundrectangle',
        'text-background-padding': 2,
        'text-wrap': 'wrap',
        'text-max-width': '200px',
        'transition-property': 'opacity',
        'transition-duration': '260ms',
      },
    },
    // Specific edge rules come AFTER the generic `edge` rule — Cytoscape applies
    // later rules last, so placed earlier they were silently overridden.
    // Counterfeit tokens and poisoning look-alikes are not money flow: dim, dashed, no arrow (the
    // Workbench draws them the same way, utils/counterfeitEdge.ts). Counterparty links are context:
    // a muted cool grey, thin, still directed.
    { selector: 'edge[edge_type="counterfeit"]', style: {
        'width': 1, 'line-color': '#a8b3c4', 'line-style': 'dashed', 'line-dash-pattern': [3, 5],
        'target-arrow-shape': 'none', 'source-arrow-shape': 'none', 'opacity': 0.55 } },
    { selector: 'edge[edge_type="counterparty"]', style: {
        'width': 1.2, 'line-color': '#7c8aa0', 'target-arrow-color': '#7c8aa0', 'opacity': 0.75 } },
    { selector: 'edge[edge_type="p2p"]', style: { 'line-color': '#9333ea', 'target-arrow-color': '#9333ea', 'width': 2.6 } },
    // A rapid sweep is a deterministic ledger fact: red and heavy, but solid —
    // dashes mean "scored" on this canvas and nothing else.
    { selector: 'edge[?is_rapid_sweep]', style: { 'line-color': '#dc2626', 'target-arrow-color': '#dc2626', 'width': 3 } },
    { selector: 'edge[edge_type="subpoena"]', style: {
        'line-color': '#2563eb', 'target-arrow-color': '#2563eb', 'target-arrow-shape': 'tee', 'width': 2 } },
    { selector: 'edge[edge_type="proof_anchor"]', style: { 'width': 4, 'line-style': 'solid' } },
    { selector: 'edge[edge_type="ownership"]', style: { 'target-arrow-shape': 'triangle-tee' } },
    // LINK02: Officer-confirmed subject links. Line colour and dash distinct from ownership,
    // affiliation and the scored guess; retracted greyed through existing _visibility preset (no opacity in rule).
    { selector: 'edge[edge_type="subject_link"]', style: {
        'line-color': '#0891b2', 'target-arrow-color': '#0891b2', 'width': 2.2,
        'line-style': 'dashed', 'line-dash-pattern': [8, 3, 2, 3] } },
    { selector: 'edge[edge_type="subject_link"][?retracted]', style: {
        'line-color': '#94a3b8', 'target-arrow-color': '#94a3b8',
        'line-style': 'dashed', 'line-dash-pattern': [3, 4], 'target-arrow-shape': 'none' } },
    // Determinism decides the dash, after every type rule: scored = dashed,
    // deterministic = solid (generic rule above).
    { selector: 'edge[determinism="scored"]', style: { 'line-style': 'dashed' } },
    // An edge a later run did not re-derive (FIX38): dashed, no arrow, whatever its type; the label says why.
    // Its opacity, like a dormant node's (RET01), comes from `_visibility` (VISIBILITY.EARLIER_RUN, R11).
    { selector: 'edge[?stale]', style: { 'line-style': 'dashed', 'target-arrow-shape': 'none' } },
    // A coverage gap (CANVAS02): the run read neither wallet, so the edge is neither confirmed nor withdrawn.
    // Grey and dashed with a longer dash than the scored guess, arrow kept (the transfer is real). Opacity
    // comes from `_visibility` (VISIBILITY.SECONDARY), never from this rule (R11).
    { selector: 'edge[?coverage_gap]', style: {
        'line-color': '#9ca3af', 'target-arrow-color': '#9ca3af', 'line-style': 'dashed', 'line-dash-pattern': [10, 5, 2, 5] } },
    // `_visibility` is the one opacity channel (constants/visibility.ts, R11). After every type rule so it wins.
    { selector: 'node[_visibility], edge[_visibility]', style: { 'opacity': 'data(_visibility)' } },
    // Edge labels come back on hover (class set by FusionCanvas) and on selection.
    { selector: 'edge.cs-label-hover, edge:selected', style: { 'min-zoomed-font-size': 0, 'z-index': 40 } },
    { selector: '.cs-bloom', style: { 'overlay-color': '#38bdf8', 'overlay-opacity': 0.55, 'overlay-padding': 11, 'z-index': 30 } },
    { selector: '.cs-dim', style: { 'opacity': 0.06, 'text-opacity': 0 } },
    { selector: '.cs-stephidden', style: { 'display': 'none' } },
    { selector: '.cs-dimmed-past', style: { 'opacity': 0.18, 'text-opacity': 0.5 } },
    { selector: '.cs-modhidden', style: { 'display': 'none' } },
    { selector: '.cs-replayhidden', style: { 'display': 'none' } },   // Journal press-play replay (Increment C)
    { selector: 'node:selected', style: { 'overlay-color': '#0ea5e9', 'overlay-opacity': 0.25, 'overlay-padding': 7, 'border-color': '#0284c7' } },
    { selector: 'edge:selected', style: { 'line-color': '#0ea5e9', 'target-arrow-color': '#0ea5e9', 'width': 4, 'opacity': 1 } },
    { selector: 'node[type="annotation"]', style: {
        'shape': 'round-rectangle', 'background-color': '#fef08a', 'border-width': 1, 'border-color': '#eab308',
        'color': '#854d0e', 'font-size': 9, 'text-valign': 'center', 'text-halign': 'center', 'text-wrap': 'wrap',
        'text-max-width': '140px', 'width': 150, 'height': (n: any) => Math.max(30, (n.data('label') || '').length * 0.4), 'text-margin-y': 0, 'z-index': 100 } },
    { selector: 'edge[edge_type="annotation_link"]', style: {
        'line-style': 'dashed', 'line-color': '#eab308', 'target-arrow-shape': 'none', 'width': 2, 'opacity': 0.8 } },
  ];
}

// ── Case-level report payload (GET /cases/{id}/report-data) ─────────────────
// Every section is optional: a key the case-worker run did not produce is null
// and the report prints "not run" / "not available" — never a guessed value.

export interface CaseReportAiDisclosure {
  generation_id?: string | null;
  provider?: string | null;
  model?: string | null;
  purpose?: string | null;
  status?: string | null;
  generated_at?: string | null;
  influenced?: string | null;
  source?: string | null;
  /** The PII policy kept this step on the server; nothing was sent out. */
  withheld_by_policy?: boolean;
  pii_policy?: string | null;
  /** Masked identifier spans per kind (or a single count) before external processing. */
  redaction_counts?: Record<string, number> | number | null;
  error?: string | null;
}

export interface CaseReportEdge {
  id?: string;
  source: string;
  target: string;
  source_label?: string | null;
  target_label?: string | null;
  edge_type?: string;
  determinism?: string;
  value?: number | null;
  token?: string | null;
  tx_hash?: string | null;
  timestamp?: string | number | null;
  dwell_seconds?: number | null;
  share_pct?: number | null;
  permit?: Record<string, unknown> | null;
  phase?: string | null;
  group?: string | null;
  assertion_source?: string | null;
  transfers?: CaseReportTransfer[];
  /** BTC01/BTC02: the line that states the case's part of a UTXO spend ("5.46 BTC of a 164.45 BTC spend (pro rata by inputs)"). */
  share_note?: string | null;
  /** The facts behind `share_note`. */
  share?: CaseReportShare | null;
}

/** One log event or output of a transaction on an edge (an edge can hold two rows of one hash). */
export interface CaseReportTransfer {
  tx_hash?: string | null;
  amount?: number | null;
  token?: string | null;
  timestamp?: string | number | null;
  /** The log event of the transaction (EVM); two indexes are two events whatever the amounts. */
  log_index?: string | number | null;
  /** UTXO row: what the spend paid the recipient in this output; `amount` is the case's part of it. */
  output_total?: number | null;
}

/** BTC02: how the case's part of a UTXO spend was reached. */
export interface CaseReportShare {
  share_basis?: 'single_output_min' | 'pro_rata_inputs' | 'case_input' | 'whole_case' | 'no_case_input' | string | null;
  output_total?: number | null;
  case_input?: number | null;
  case_share?: number | null;
  total_input?: number | null;
  output_count?: number | null;
  /** True when the spend's output count was not known and was read as one output (the part is an upper bound). */
  output_count_unknown?: boolean | null;
  case_amount?: number | null;
  /** RAIL04 / FIX45: `not_case_funds` on a leg that carries none of the case's money (its part is 0, never totalled). */
  case_status?: string | null;
}

export interface CaseReportNode {
  id: string;
  label?: string | null;
  type?: string | null;
  module?: string | null;
  chain?: string | null;
  address?: string | null;
  semantic_role?: string | null;
  phase?: string | null;
  group?: string | null;
  /** RET01: every edge of this wallet is an earlier run's; listed apart, never dropped. */
  dormant?: boolean;
  dormant_since_run?: string | null;
  dormant_since?: string | null;
  seizure_target?: boolean;
}

/** CONS01: how many independent source families name a wallet (information, never a status). */
export interface LabelConsensus {
  sources?: number | null;
  families?: string[] | null;
  single_source?: boolean | null;
}

/** CONS01: the grade of one sanctions hop path, as the backend attaches it to a multi-hop hit. */
export interface SanctionsPathGrade {
  grade?: 'probative' | 'supported' | 'circumstantial' | 'untested' | string | null;
  /** e.g. "3 hops, legs 9 and 2 days apart, amounts unrelated: circumstantial". */
  text?: string | null;
  hop_count?: number | null;
  timing_window_s?: number | null;
  amount_relation?: string | null;
  shared_counterparties?: number | null;
}

/** CONS01: one sanctions hop path of the `sanctions_paths` block (`analysis/sanctions_path.py`). */
export interface SanctionsPath extends SanctionsPathGrade {
  root?: string | null;
  sanctioned?: string | null;
  sanctioned_basis?: 'programme_index' | 'entity_label' | 'vendor_exposure' | string | null;
  direction?: string | null;
  legs?: string[] | null;
  programs?: string[] | null;
  leg_gaps_s?: (number | null)[] | null;
  order?: string | null;
}

export interface SanctionsPathsBlock {
  schema?: string | null;
  window_s?: number | null;
  amount_tolerance?: number | null;
  counts?: Record<string, number> | null;
  paths?: SanctionsPath[] | null;
}

/** One seizure-matrix row. Extra keys stay so older runs still render. */
export interface SeizureTarget {
  address?: string | null;
  chain?: string | null;
  entity_name?: string | null;
  brand?: string | null;
  wallet_role?: string | null;
  label_class?: string | null;
  name_status?: string | null;
  confirmation_state?: string | null;
  /** ROLE03: the direction test of the row wallet's role, when the run made it. */
  role_check?: RoleCheck | null;
  /** CONS01: the label's independent source families, when the run holds a named entity for the wallet. */
  consensus?: LabelConsensus | null;
  case_share_basis?: string | null;
  /** True on a row that still needs a linking transfer. The matrix-level list is the leads. */
  corroboration_needed?: boolean | null;
  nexus_role?: string | null;
  [key: string]: unknown;
}

export interface CaseReportData {
  schema_version?: string;
  case_id: string;
  case: Record<string, unknown> | null;
  latest_run: {
    event_id?: string | null;
    ts?: string | null;
    actor_id?: number | null;
    run_meta?: Record<string, unknown> | null;
    status?: string | null;
    summary?: Record<string, unknown> | null;
    case_title?: string | null;
    has_investigation_file?: boolean;
  } | null;
  graph_high: {
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
    phases?: { id: string; label: string; order: number; node_count?: number }[];
    groups?: Record<string, unknown>[];
    counts?: Record<string, number>;
  } | null;
  graph_low_counts: Record<string, number> | null;
  /** Edges marked stale by a later run (FIX38): excluded from the totals, listed here. Count 0 (explicit) when the run marked none (FIX40). */
  stale_edges?: {
    count: number;
    by_reason: Record<string, number>;
    line: string;
    excluded_from_totals: boolean;
    edges: { id?: string; edge_id?: string; source?: string; target?: string; edge_type?: string; reason?: string; since_run?: string; last_run_ref?: string }[];
    edges_truncated?: boolean;
  } | null;
  /** RET01: wallets whose every edge is an earlier run's; out of `graph_low_counts.nodes`. */
  dormant_nodes?: {
    active_nodes: number;
    dormant_nodes: number;
    dormant_since?: { first_run?: string | null; first_at?: string | null; last_run?: string | null; last_at?: string | null; runs?: number } | null;
    line?: string;
    excluded_from_totals?: boolean;
  } | null;
  phases?: { id: string; label: string; order: number; node_count?: number }[];
  groups?: Record<string, unknown>[];
  nodes?: CaseReportNode[];
  edge_list?: CaseReportEdge[];
  run_flows?: Record<string, unknown>[] | null;
  seizure_targets: {
    targets: SeizureTarget[];
    total_targets?: number | null;
    serveable_count?: number | null;
    estimated_freezable_usd?: number | null;
    summary?: string | null;
    /** Rows that need a linking transfer before they can be served; drawn in the subpoena figure as leads. */
    corroboration_needed?: SeizureTarget[];
    corroboration_needed_count?: number | null;
    /** NEXUS02: rows carried from the previous run sit in corroboration_needed (`carried_from_prior_run`); this counts them. */
    carried_count?: number | null;
    carry?: { status: string; max_runs: number; carried: number; expired: number; reproduced: number; prior_run_id?: string | null; prior_recorded_at?: string | null; error?: string } | null;
    /** Issuer rows below the row cap; not targets and not drawn. */
    deferred_count?: number | null;
  } | null;
  recheck_gates: unknown;
  value_reconciliation?: unknown;
  flow_diagrams: unknown;
  feeder_profiles: unknown;
  corridor: unknown;
  handshake_tests: unknown;
  resource_events: unknown;
  ai_disclosures: CaseReportAiDisclosure[];
  certificate_65b: unknown;
  screening?: unknown;
  exchange_notices?: ExchangeNoticesRecord | null;
  ats_nexus?: AtsNexusRecord | null;
}

export type ExchangeNoticeRelation = 'forward_from_subject' | 'prior_to_subject' | 'feeder_hop' | 'hub_offramp';
export type ExchangeNoticeNameStatus = 'registry' | 'vendor_lead' | 'stripped_sweep' | 'customer_label' | 'role_unconfirmed';
export type ExchangeNoticeCaseShare = 'victim_funds' | 'not_the_traced_amount';
export type ExchangeNoticeRole = 'exchange_wallet' | 'sweep_address' | 'card_gateway';

export interface NoticeRow {
  relation?: ExchangeNoticeRelation | string | null;
  hop?: number | null;
  /** Hops along the victim-funds walk (table 1 rows only). */
  forward_hop?: number | null;
  role?: ExchangeNoticeRole | string | null;
  /** Role of the cited wallet: exchange_hot, exchange_deposit, card_offramp, customer_wallet, subject, feeder, hub or unknown. */
  wallet_role?: string | null;
  /** NOTICE08: what the role rests on (entity_record, roles, hub_role, label, exit_kind); null when unknown. */
  wallet_role_source?: string | null;
  /** NOTICE08: why the role is unknown (no_label, no_role, conflicting_roles, role_contradicted, role_unresolved). */
  wallet_role_reason?: string | null;
  wallet_role_candidates?: string[] | null;
  /** NOTICE08: the flow or pass that decided the relation (forward_walk, hub_offramp_edge, pre_case_transfer, ...). */
  relation_source?: string | null;
  /** NOTICE09: `issuer_freeze` for a request to a stablecoin issuer to blacklist the holding address. */
  notice_kind?: 'issuer_freeze' | string | null;
  brand?: string | null;
  chain?: string | null;
  /** NOTICE09: the issuer's registered token contract on `chain` (null when the registry holds none). */
  issuer_contract?: string | null;
  /** NOTICE09: the holder wallet an issuer row asks the issuer to freeze. */
  wallet?: string | null;
  holder_is_subject?: boolean | null;
  /** NOTICE09: live_balance, attributed_share, aggregate or last_known. */
  amount_basis?: string | null;
  case_amount?: number | null;
  blacklisted?: boolean | null;
  evidence_source?: string | null;
  also_cited?: Array<{ amount?: number | null; amount_basis?: string | null; tx_hash?: string | null; evidence_source?: string | null }> | null;
  label_class?: string | null;
  /** Whether the cited transfer goes into ('in') or out of ('out') the cited wallet. */
  direction?: 'in' | 'out' | string | null;
  exchange_name?: string | null;
  name_source?: string | null;
  name_status?: ExchangeNoticeNameStatus | string | null;
  exchange_wallet?: string | null;
  outside_wallet?: string | null;
  tx_hash?: string | null;
  amount?: number | null;
  token?: string | null;
  timestamp?: string | null;
  case_share?: ExchangeNoticeCaseShare | string | null;
  ask?: string | null;
  note?: string | null;
  /** BAL02: the live balance read for this wallet at report time, present only when the run read one. */
  live_read?: NoticeLiveRead | null;
  /** NOTICE07: the id of the feeder summary this row belongs to, and the row's id inside it. */
  feeder_group?: string | null;
  notice_row_id?: string | null;
  /** ROLE03: present when the direction test contradicted the wallet's role (the row then names the brand only). */
  role_check?: RoleCheck | null;
  /** SIG01: the behaviour tier of the feeder this row is about (test_then_bulk, single_drop, periodic, irregular). */
  feeder_behaviour?: string | null;
  /** SIG01: the feeders of that tier the row's transfers reach. */
  feeders?: string[] | null;
  /** CONS01: the cited wallet's label consensus; a single-source registry row says so in its note. */
  consensus?: LabelConsensus | null;
  /** NOTICE05: the registry template the notice is drafted on. */
  template_key?: string | null;
  /** NOTICE05: only the fields the engine holds, each with its provenance (the value is the row's own field). */
  asserted?: NoticeAssertedField[] | null;
  /** NOTICE05: what the row carried that the engine does not hold (or holds without a source); kept out of Asserted. */
  refused?: Array<{ field?: string | null; reason?: string | null }> | null;
  /** NOTICE05: what the notice asks the recipient to produce, keyed to the cited wallet and transaction. */
  requested?: NoticeRequestedField[] | null;
  /** NOTICE05B: the officer-completes line printed on every rendered notice. */
  counsel_review?: string | null;
}

export interface NoticeAssertedField {
  field?: string | null;
  value?: unknown;
  provenance?: string | null;
}

export interface NoticeRequestedField {
  key?: string | null;
  field?: string | null;
  basis?: string | null;
  why?: string | null;
  keyed_to?: { address?: string | null; tx_hash?: string | null } | null;
}

/** ROLE03: the direction test of a wallet role against the wallet's own transfers. */
export interface RoleCheck {
  verdict?: 'consistent' | 'contradicted' | 'insufficient' | string | null;
  reason?: string | null;
}

export interface NoticeLiveRead {
  balance?: number | null;
  token?: string | null;
  /** swept | holds_balance | blacklisted | omnibus (a pooled exchange wallet: not seizable as such). */
  rule?: string | null;
  read_at?: string | null;
}

export interface ExchangeNoticesRecord {
  subject_id?: string | null;
  subject_first_inbound?: string | null;
  victim_path?: NoticeRow[] | null;
  associated?: NoticeRow[] | null;
  /** NOTICE07: one summary per feeder that fed two or more deposit wallets of one exchange brand. */
  feeder_summaries?: FeederSummary[] | null;
  /** NOTICE09: the issuer freeze requests counted apart from the exchange notices. */
  issuer_summary?: { count: number; by_brand: Record<string, number>; line: string } | null;
}

/** NOTICE07: `exchange_notices.feeder_summaries[]` (analysis/exchange_notice.py). Totals are per token, never summed across tokens. */
export interface FeederSummary {
  id: string;
  feeder: string;
  brand?: string | null;
  /** NOTICE07-b: the distinct raw labels of the group's rows. */
  labels?: string[] | null;
  deposit_wallets?: string[] | null;
  deposit_wallet_count?: number | null;
  transfers?: number | null;
  total_by_token?: Record<string, number> | null;
  first?: string | null;
  last?: string | null;
  /** The member rows' relation when all agree, else `mixed`. */
  relation?: string | null;
  row_ids?: string[] | null;
  cited_basis_counts?: Record<string, number> | null;
  /** The one-line summary the report and the served notice print. */
  line?: string | null;
}

/** Direct hit, keyed as `analysis/ats_screening.py` emits it. */
export interface AtsDirectHit {
  node_id: string;
  address: string;
  flow_context?: string;
  programs: string[];
  buckets: string[];
  sdn_name?: string | null;
  match_type?: string;
  evidence_note?: string;
  /** Hash of the graph transfer that connects the node; set on a counted hit. */
  connecting_tx_hash?: string | null;
  evidence?: string;
}

export interface AtsMultiHopHit {
  root_address: string;
  target_address?: string | null;
  direction?: string;
  hop_path: string;
  hop_count: number;
  program: string;
  bucket: string;
  sdn_name?: string | null;
  flow_context: string;
  evidence_note: string;
  connecting_tx_hash?: string | null;
  /** `tx_hash` on a counted hit; `vendor_path_only` / `graph_node_no_hash` on a lead. */
  evidence?: string;
  hash_source?: string;
  /** MAT01: the vendor exposure row the hit came from (absent on a hop-path hit). */
  exposure_entity?: string | null;
  exposure_risk_type?: string | null;
  exposure_type?: string | null;
  exposure_hop?: number | string | null;
  /** MAT01: the materiality the run graded for that exposure row. */
  materiality?: ExposureMateriality | null;
  /** CONS01: the grade of the hit's sanctions path (timing, amounts, shared counterparties). */
  path_grade?: SanctionsPathGrade | null;
}

/** MAT01: one exposure's materiality as the ATS screen carries it. */
export interface ExposureMateriality {
  grade?: 'material' | 'immaterial' | 'unknown' | string | null;
  grade_reason?: string | null;
  absolute_usd?: number | null;
  share_of_volume?: number | null;
  denominator_basis?: string | null;
}

/** MAT01: one row of the run's `exposure_materiality` block. */
export interface ExposureMaterialityRow extends ExposureMateriality {
  address?: string | null;
  chain?: string | null;
  risk_source?: string | null;
  entity?: string | null;
  risk_type?: string | null;
  exposure_type?: string | null;
  hop_num?: number | string | null;
  value_field?: string | null;
  denominator_usd?: number | null;
  vendor_percent?: number | null;
}

export interface ExposureMaterialityBlock {
  schema?: string;
  thresholds?: { material_share?: number | null; material_usd?: number | null } | null;
  counts?: Record<string, number> | null;
  rows?: ExposureMaterialityRow[] | null;
  wallets?: { address?: string | null; volume_usd?: number | null; transfers?: number | null; basis?: string | null }[] | null;
}

/** MAT01: the dwell and settlement-cycle figures of one traced intermediary. */
export interface DwellCycleWallet {
  address?: string | null;
  token?: string | null;
  hop?: number | null;
  first_arrival?: string | null;
  first_arrival_tx?: string | null;
  first_departure?: string | null;
  first_departure_tx?: string | null;
  dwell_s?: number | null;
  hold_days?: number | null;
  arrivals?: number | null;
  sweep_count?: number | null;
  sweep_interval_s?: { median?: number | null; spread?: number | null; intervals?: number | null } | null;
  periodic?: boolean | null;
  periodic_reason?: string | null;
  last_sweep?: string | null;
  reverse_flows?: { from_address?: string | null; tx_hash?: string | null; timestamp?: string | null; value?: number | null; token?: string | null }[] | null;
  reverse_flow_count?: number | null;
  readings?: string[] | null;
  text?: string | null;
}

export interface DwellCycleBlock {
  schema?: string;
  status?: 'tested' | 'not_tested' | string;
  not_tested_reason?: string | null;
  params?: Record<string, number> | null;
  counts?: Record<string, number> | null;
  wallets?: DwellCycleWallet[] | null;
  wallets_truncated?: number | null;
}

/** A match with no connecting transaction hash: listed apart, never counted. */
export interface AtsLead extends Partial<AtsDirectHit>, Partial<AtsMultiHopHit> {
  lead_kind?: 'direct_node' | 'multi_hop';
  evidence: string;
}

export interface AtsDepositTriad {
  deposit_wallet: string;
  deposit_brand: string;
  deposit_source: string;
  hot_wallet: string;
  hot_brand: string;
  tx_hash?: string | null;
  amount?: number | null;
  token?: string | null;
  timestamp?: string | null;
  ask?: string | null;
}

/** `metasleuth.risk_reads` (ATS03, ATS05, ATS06): what the risk-read pass did. Every key is optional; older runs lack most. */
export interface RiskReadsBlock {
  status?: 'ran' | 'no_candidates' | 'off' | 'blocked' | 'error' | string | null;
  reason?: string | null;
  candidates?: number | null;
  stored?: number | null;
  fresh?: number | null;
  refused?: number | null;
  refused_reason?: string | null;
  missed?: number | null;
  errors?: number | null;
  cap?: number | null;
  skipped?: number | null;
  skipped_reasons?: Record<string, number> | null;
  blocked_reason?: string | null;
  error?: string | null;
  /** RISK01: metasleuth | misttrack | composite | stored_only (no risk key, stored answers only); absent on older reports. */
  provider?: 'metasleuth' | 'misttrack' | 'composite' | 'stored_only' | string | null;
  /** RISK01: the vendors actually asked this run. */
  providers_asked?: string[] | null;
}

export interface AtsNexusRecord {
  /** False when the screen could not run (no risk map, no index, no edges): NOT TESTED, never clear (R10). */
  tested?: boolean;
  sources_consulted?: string[];
  not_tested_reason?: string | null;
  /** Hits per programme family over the direct and multi-hop hits (ATS05); absent on older reports. */
  hits_by_family?: Record<string, number>;
  /** The most severe family present among the hits; null or absent when there is no hit. */
  highest_family?: string | null;
  /** The screen ran, but something about its input needs saying (e.g. the risk provider rejected some requests). */
  caveats?: string[];
  has_ats_nexus: boolean;
  summary: {
    direct_hits_count: number;
    multi_hop_hits_count: number;
    deposit_to_hot_triad_count: number;
    categories_detected: string[];
    lead_count?: number;
    risk_answers_total?: number;
    risk_answers_on_graph?: number;
  };
  direct_node_hits: AtsDirectHit[];
  multi_hop_liquidity_hits: AtsMultiHopHit[];
  leads?: AtsLead[];
  deposit_to_hot_triads: AtsDepositTriad[];
  /** MAT01: counts of the exposure-derived hits and leads by materiality grade, with the thresholds. */
  materiality_summary?: { material?: number; immaterial?: number; unknown?: number; thresholds?: { material_share?: number | null; material_usd?: number | null } } | null;
}

